import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, Minus, RefreshCw, AlertTriangle } from 'lucide-react';
import { fetchCaptureTrace, captureGap, type CaptureTraceRow } from '@/lib/admin/capture-trace-api';

function Flag({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs ${ok ? 'text-foreground' : 'text-muted-foreground'}`}
      title={label}
    >
      {ok ? <Check className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
      {label}
    </span>
  );
}

export default function CaptureTracePanel({ orgId }: { orgId: string }) {
  const [rows, setRows] = useState<CaptureTraceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await fetchCaptureTrace(orgId, 50);
    setRows(data);
    setError(error?.message ?? null);
    setLoading(false);
  };

  useEffect(() => {
    if (orgId) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const gaps = rows.filter((r) => captureGap(r) !== null).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base font-heading">Data capture trace</CardTitle>
          <CardDescription className="text-xs">
            Per conversation: transcript, scores, summary, themes and follow-up items. Counts only,
            no content and no identity.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="text-xs text-destructive">{error}</p>}
        {!loading && rows.length === 0 && !error && (
          <p className="text-xs text-muted-foreground">No conversations yet.</p>
        )}
        {gaps > 0 && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            <AlertTriangle className="h-3.5 w-3.5" />
            {gaps} conversation{gaps === 1 ? '' : 's'} with a transcript but incomplete processing.
          </div>
        )}
        <div className="divide-y">
          {rows.map((r) => {
            const gap = captureGap(r);
            return (
              <div key={r.conversation_id} className="py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                <span className="text-xs font-mono text-muted-foreground w-[9rem] shrink-0">
                  {new Date(r.started_at).toLocaleString()}
                </span>
                <Flag ok={r.turn_count > 0} label={`transcript ${r.turn_count}`} />
                <Flag ok={r.anchor_score_count > 0} label={`scores ${r.anchor_score_count}`} />
                <Flag ok={r.pulse_count > 0} label={`pulses ${r.pulse_count}`} />
                <Flag ok={r.has_summary} label="summary" />
                <Flag ok={r.insight_unit_count > 0} label={`themes ${r.insight_unit_count}`} />
                <Flag ok={r.action_item_count > 0} label={`actions ${r.action_item_count}`} />
                {gap && (
                  <Badge variant="destructive" className="text-[10px]">
                    {gap}
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
